import { cva, cx } from "class-variance-authority";
import type { PropsWithChildren } from "react";
import { Dialog as AriaDialog, Modal as AriaModal, ModalOverlay as AriaModalOverlay } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { CloseIcon } from "@/assets/icons/general/Close";
import { InlineIconButton } from "@/components/ui/buttons/InlineIconButton/InlineIconButton";
import { Typography } from "@/components/ui/text/Typography/Typography";

const modalContent = cva(
  ["relative flex w-full flex-col items-center t:px-5 t:py-8", "bg-elevation-background shadow-5 outline-none"],
  {
    variants: {
      aside: {
        left: "h-[100svh] rounded-none",
        right: "h-[100svh] rounded-none",
        center: "max-h-[100svh] dm:rounded-none t:rounded-6xl",
        bounce: "max-h-[100svh] dm:rounded-none t:rounded-6xl",
        transparent: "bg-transparent shadow-[none]",
        chat: "dm:rounded-none t:rounded-6xl",
      },
    },
    defaultVariants: {
      aside: "center",
    },
  },
);

const modalOverlay = cva(
  [
    "fixed inset-0 z-30 flex w-screen dm:items-end t:items-center bg-elevation-surface-2/80 backdrop-blur-2",
    "h-[100svh]",
  ],
  {
    variants: {
      aside: {
        left: "justify-start p-0",
        right: "justify-end p-0",
        center: "justify-center dm:p-0 t:p-4",
        bounce: "justify-center dm:p-0 t:p-4",
        transparent: "dm:items-center justify-center dm:p-0 t:p-4",
        chat: "h-[--visual-viewport-height] justify-center p-0 transition-all duration-75",
      },
    },
    defaultVariants: {
      aside: "center",
    },
  },
);

const modalMain = cva(["w-auto max-w-modal"], {
  variants: {
    aside: {
      left: "entering:animate-drawer-enter-left exiting:animate-drawer-exit-left",
      right:
        "fixed top-0 bottom-0 dm:w-full t:w-auto entering:animate-drawer-enter-right exiting:animate-drawer-exit-right",
      center: "",
      transparent: "",
      chat: "",
      bounce: "dm:max-w-[100vw] t:max-w-[80vw] entering:animate-overlay-bounce-in exiting:animate-overlay-bounce-out",
    },
  },
  defaultVariants: {
    aside: "center",
  },
});

export type ModalProps = {
  isOpen: boolean;
  className?: string;
  portalContainerRef?: React.RefObject<HTMLElement>;
  onClose: () => void;
  aside?: "left" | "right" | "transparent" | "chat" | "bounce";
  title?: string;
  hideCloseButton?: boolean;
  modalClassName?: string;
};

export const Modal = ({
  isOpen,
  className,
  portalContainerRef,
  onClose,
  aside,
  children,
  title,
  hideCloseButton,
  modalClassName,
}: PropsWithChildren<ModalProps>) => {
  const { t } = useTranslation();

  return (
    <AriaModalOverlay
      UNSTABLE_portalContainer={portalContainerRef?.current || undefined}
      className={cx(modalOverlay({ aside }), className)}
      isDismissable
      isOpen={isOpen}
      shouldCloseOnInteractOutside={() => {
        return !portalContainerRef?.current;
      }}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <AriaModal className={modalMain({ aside })}>
        <AriaDialog
          className={cx(modalContent({ aside }), modalClassName)}
          aria-label="modal"
        >
          {(!hideCloseButton || title) && (
            <div className="relative dm:m-4 t:m-0 flex w-full items-center justify-center">
              {!hideCloseButton && (
                <InlineIconButton
                  onPress={onClose}
                  icon={CloseIcon}
                  className="absolute t:top-1 right-0 z-10 p-0 text-text-default-primary"
                  label={t("ui.modal.closeBtn")}
                />
              )}
              {title && (
                <Typography
                  variant="prominent-2"
                  size="title-5"
                  className="top-0 right-0 left-0 t:max-w-md px-9 text-center font-body-prominent-2 text-text-default-primary"
                >
                  {title}
                </Typography>
              )}
            </div>
          )}

          {children}
        </AriaDialog>
      </AriaModal>
    </AriaModalOverlay>
  );
};
